import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { injectModalRef } from '../../../../../shared/modal';
import { AdminCallRow } from '../../../models/admin-call.model';
import { AdminOrgApiKey } from '../../../models/admin-organization-detail.model';
import { AdminService } from '../../../services/admin.service';

export interface AdminKeyCallsModalData {
  key: AdminOrgApiKey;
  orgName: string;
}

const PER_PAGE = 25;

/**
 * One API key's calls, opened full-screen from Organization Detail's API
 * keys tab (bound to `?key=`, see bindModalToQueryParam there). Same
 * GET /admin/calls?api_key_id= the Calls page uses, paged here.
 */
@Component({
  selector: 'app-admin-key-calls-modal',
  standalone: false,
  templateUrl: './admin-key-calls-modal.html',
  styleUrl: './admin-key-calls-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminKeyCallsModal implements OnInit {
  readonly data = input.required<AdminKeyCallsModalData>();
  readonly modalRef = injectModalRef<void>();

  private readonly adminService = inject(AdminService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly rows = signal<AdminCallRow[]>([]);
  protected readonly page = signal(1);
  protected readonly pages = signal(1);
  protected readonly total = signal(0);
  protected readonly loading = signal(true);
  protected readonly failed = signal(false);

  ngOnInit(): void {
    this.load(1);
  }

  protected load(page: number): void {
    this.loading.set(true);
    this.failed.set(false);
    this.adminService.getCalls({ apiKeyId: this.data().key.id, page, perPage: PER_PAGE })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ rows, pagination }) => {
          this.rows.set(rows);
          this.page.set(pagination.page ?? page);
          this.pages.set(pagination.pages || 1);
          this.total.set(pagination.total ?? rows.length);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.failed.set(true);
        },
      });
  }
}
